import { useState, useEffect, useCallback } from 'react'
import { adminActivities, type ActivityItem } from '../api/client'
import { styles as as } from './adminStyles'

interface Props {
  onBack: () => void
}

interface Draft {
  emoji:        string
  esWord:       string
  esDefinition: string
  enWord:       string
  enDefinition: string
}

const BLANK: Draft = { emoji: '', esWord: '', esDefinition: '', enWord: '', enDefinition: '' }

// Canonical source for classic items — all three activities share the same data.
const CANONICAL_SLUG = 'memory'
const SHARED_SLUGS   = ['memory', 'wordmatch', 'wordsearch'] as const

function nextId(items: ActivityItem[]): number {
  return items.length === 0 ? 1 : Math.max(...items.map((i) => i.id)) + 1
}

function draftFromItem(item: ActivityItem): Draft {
  return {
    emoji:        item.emoji,
    esWord:       item.es.word,
    esDefinition: item.es.definition,
    enWord:       item.en.word,
    enDefinition: item.en.definition,
  }
}

export default function ClassicThemeEditor({ onBack }: Props) {
  const [items,     setItems]     = useState<ActivityItem[]>([])
  const [loading,   setLoading]   = useState(true)
  const [saving,    setSaving]    = useState(false)
  const [error,     setError]     = useState<string | null>(null)
  const [savedOk,   setSavedOk]   = useState(false)
  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [draft,     setDraft]     = useState<Draft>(BLANK)
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    adminActivities.get(CANONICAL_SLUG)
      .then((a) => setItems(a.data?.items ?? []))
      .catch(() => setError('Error al cargar las tarjetas'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const startEdit = (item: ActivityItem) => {
    setConfirmDelete(null)
    setEditingId(item.id)
    setDraft(draftFromItem(item))
  }

  const startNew = () => {
    setConfirmDelete(null)
    setEditingId('new')
    setDraft(BLANK)
  }

  const cancelEdit = () => { setEditingId(null); setDraft(BLANK) }

  const commitEdit = () => {
    const item: ActivityItem = {
      id:    editingId === 'new' ? nextId(items) : editingId as number,
      emoji: draft.emoji.trim() || '❓',
      es:    { word: draft.esWord.trim(), definition: draft.esDefinition.trim() },
      en:    { word: draft.enWord.trim(), definition: draft.enDefinition.trim() },
    }
    setItems((prev) =>
      editingId === 'new'
        ? [...prev, item]
        : prev.map((i) => i.id === item.id ? item : i),
    )
    cancelEdit()
  }

  const deleteItem = (id: number) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
    if (editingId === id) cancelEdit()
    setConfirmDelete(null)
  }

  const moveItem = (id: number, dir: -1 | 1) => {
    setItems((prev) => {
      const idx    = prev.findIndex((i) => i.id === id)
      const newIdx = idx + dir
      if (idx < 0 || newIdx < 0 || newIdx >= prev.length) return prev
      const next = [...prev]
      ;[next[idx], next[newIdx]] = [next[newIdx], next[idx]]
      return next
    })
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    setSavedOk(false)
    try {
      await Promise.all(SHARED_SLUGS.map((s) => adminActivities.patch(s, { data: { items } })))
      setSavedOk(true)
      setTimeout(() => setSavedOk(false), 3000)
    } catch {
      setError('Error al guardar las tarjetas')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={as.page}>
      <header style={as.header}>
        <button onClick={onBack} style={as.backBtn}>← Actividades</button>
        <div>
          <h1 style={as.headerTitle}>Tarjetas clásicas</h1>
          <p style={s.sharedNote}>Compartidas por Memory · Word Match · Word Search</p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {savedOk && <span style={s.savedBadge}>✓ Guardado</span>}
          <button
            onClick={save}
            disabled={saving || loading}
            style={{ ...as.submitBtn, opacity: (saving || loading) ? 0.6 : 1, minHeight: 40 }}
          >
            {saving ? 'Guardando…' : `Guardar (${items.length})`}
          </button>
        </div>
      </header>

      <div style={as.content}>
        {error && <div style={s.errorBanner}>{error}</div>}

        {loading ? (
          <p style={as.emptyText}>Cargando…</p>
        ) : (
          <>
            <div style={s.cardList}>
              {items.map((item, idx) => (
                <div key={item.id}>
                  {editingId === item.id ? (
                    <EditForm
                      draft={draft}
                      onChange={setDraft}
                      onSave={commitEdit}
                      onCancel={cancelEdit}
                    />
                  ) : (
                    <div style={s.cardRow}>
                      <span style={s.cardEmoji}>{item.emoji}</span>
                      <div style={s.cardWords}>
                        <span style={s.wordEs}>{item.es.word}</span>
                        <span style={s.wordSep}>/</span>
                        <span style={s.wordEn}>{item.en.word}</span>
                        <span style={s.defEs}>{item.es.definition}</span>
                      </div>
                      <div style={s.cardActions}>
                        <button
                          onClick={() => moveItem(item.id, -1)}
                          disabled={idx === 0}
                          style={{ ...s.iconBtn, opacity: idx === 0 ? 0.3 : 1 }}
                          aria-label="Subir"
                        >↑</button>
                        <button
                          onClick={() => moveItem(item.id, 1)}
                          disabled={idx === items.length - 1}
                          style={{ ...s.iconBtn, opacity: idx === items.length - 1 ? 0.3 : 1 }}
                          aria-label="Bajar"
                        >↓</button>
                        <button
                          onClick={() => startEdit(item)}
                          style={as.editBtn}
                          disabled={!!editingId}
                          aria-label="Editar"
                        >Editar</button>
                        {confirmDelete === item.id ? (
                          <>
                            <button onClick={() => deleteItem(item.id)} style={s.deleteConfirmBtn}>
                              Eliminar
                            </button>
                            <button onClick={() => setConfirmDelete(null)} style={as.cancelBtn}>
                              No
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setConfirmDelete(item.id)}
                            style={as.deleteIconBtn}
                            disabled={!!editingId}
                            aria-label="Eliminar"
                          >🗑</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {editingId === 'new' ? (
                <EditForm
                  draft={draft}
                  onChange={setDraft}
                  onSave={commitEdit}
                  onCancel={cancelEdit}
                />
              ) : (
                <button onClick={startNew} style={s.addBtn} disabled={!!editingId}>
                  + Nueva tarjeta
                </button>
              )}
            </div>

            {items.length === 0 && editingId !== 'new' && (
              <p style={as.emptyText}>No hay tarjetas. Añade la primera con el botón de arriba.</p>
            )}

            <p style={s.hint}>
              Recuerda guardar los cambios con el botón superior. Se necesitan al menos 12 tarjetas para jugar.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

/* ── Edit form ───────────────────────────────────────────────── */

interface EditFormProps {
  draft:    Draft
  onChange: (d: Draft) => void
  onSave:   () => void
  onCancel: () => void
}

function EditForm({ draft, onChange, onSave, onCancel }: EditFormProps) {
  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange({ ...draft, [k]: e.target.value })

  return (
    <div style={s.editForm}>
      <div style={s.editRow}>
        <div style={{ ...as.formField, flex: '0 0 90px' }}>
          <label style={as.label}>Emoji</label>
          <input
            value={draft.emoji}
            onChange={set('emoji')}
            placeholder="🌸"
            style={{ ...as.input, fontSize: '1.6rem', textAlign: 'center' }}
            maxLength={4}
          />
        </div>
        <div style={{ ...as.formField, flex: 1 }}>
          <label style={as.label}>Palabra (ES)</label>
          <input value={draft.esWord} onChange={set('esWord')} placeholder="Flor de cerezo" style={as.input} />
        </div>
        <div style={{ ...as.formField, flex: 1 }}>
          <label style={as.label}>Word (EN)</label>
          <input value={draft.enWord} onChange={set('enWord')} placeholder="Blossom" style={as.input} />
        </div>
      </div>
      <div style={s.editRow}>
        <div style={{ ...as.formField, flex: 1 }}>
          <label style={as.label}>Definición (ES)</label>
          <textarea
            value={draft.esDefinition}
            onChange={set('esDefinition')}
            placeholder="Hermosa flor que florece en primavera"
            style={{ ...as.input, resize: 'vertical', minHeight: 60 }}
            rows={2}
          />
        </div>
        <div style={{ ...as.formField, flex: 1 }}>
          <label style={as.label}>Definition (EN)</label>
          <textarea
            value={draft.enDefinition}
            onChange={set('enDefinition')}
            placeholder="A flower, or the mass of flowers on a tree"
            style={{ ...as.input, resize: 'vertical', minHeight: 60 }}
            rows={2}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onSave} style={as.submitBtn}>Guardar tarjeta</button>
        <button onClick={onCancel} style={as.cancelBtn}>Cancelar</button>
      </div>
    </div>
  )
}

/* ── Local styles ────────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  errorBanner: {
    backgroundColor: '#FEE2E2',
    color:           '#991B1B',
    border:          '1px solid #FCA5A5',
    borderRadius:    10,
    padding:         '12px 16px',
    marginBottom:    20,
    fontSize:        '0.9rem',
  },
  savedBadge: {
    fontSize:   '0.88rem',
    fontWeight: 700,
    color:      '#52B788',
  },
  cardList: {
    display:       'flex',
    flexDirection: 'column',
    gap:           8,
    marginBottom:  24,
  },
  cardRow: {
    display:      'flex',
    alignItems:   'center',
    gap:          14,
    background:   '#FFFFFF',
    border:       '1.5px solid #EFE7D7',
    borderRadius: 12,
    padding:      '12px 16px',
    boxShadow:    '0 1px 4px rgba(27,67,50,0.05)',
  },
  cardEmoji: {
    fontSize:   '1.8rem',
    lineHeight: 1,
    flexShrink: 0,
    minWidth:   36,
    textAlign:  'center',
  },
  cardWords: {
    flex:       1,
    display:    'flex',
    alignItems: 'baseline',
    gap:        6,
    flexWrap:   'wrap',
    minWidth:   0,
  },
  wordEs: {
    fontSize:   '0.95rem',
    fontWeight: 700,
    color:      '#2E1F14',
    whiteSpace: 'nowrap',
  },
  wordSep: { fontSize: '0.875rem', color: '#C0AFA0' },
  wordEn: {
    fontSize:   '0.9rem',
    fontWeight: 600,
    color:      '#6B4C3B',
    whiteSpace: 'nowrap',
  },
  defEs: {
    fontSize: '0.875rem',
    color:      '#9A8070',
    width:      '100%',
    marginTop:  2,
    lineHeight: 1.3,
  },
  cardActions: {
    display:    'flex',
    alignItems: 'center',
    gap:        6,
    flexShrink: 0,
  },
  iconBtn: {
    minWidth:       36,
    minHeight:      36,
    background:     '#F7F3EE',
    border:         '1.5px solid #EFE7D7',
    borderRadius:   8,
    cursor:         'pointer',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontSize:       '0.9rem',
  },
  deleteConfirmBtn: {
    fontSize: '0.875rem',
    fontWeight:   700,
    color:        '#991B1B',
    background:   '#FEE2E2',
    border:       '1.5px solid #FCA5A5',
    borderRadius: 8,
    padding:      '6px 12px',
    cursor:       'pointer',
    minHeight:    36,
  },
  editForm: {
    background:    '#FFFFFF',
    border:        '2px solid #B7E4C7',
    borderRadius:  14,
    padding:       '20px',
    display:       'flex',
    flexDirection: 'column',
    gap:           14,
    boxShadow:     '0 2px 12px rgba(27,67,50,0.08)',
  },
  editRow: {
    display:  'flex',
    gap:      12,
    flexWrap: 'wrap',
  },
  addBtn: {
    fontSize:     '0.9rem',
    fontWeight:   700,
    color:        '#2D6A4F',
    background:   '#F0FAF4',
    border:       '2px dashed #B7E4C7',
    borderRadius: 12,
    padding:      '14px 20px',
    cursor:       'pointer',
    textAlign:    'center',
    minHeight:    52,
    width:        '100%',
  },
  hint: {
    fontSize: '0.875rem',
    color:      '#9A8070',
    fontStyle:  'italic',
    lineHeight: 1.5,
    marginTop:  8,
  },
  sharedNote: {
    fontSize: '0.875rem',
    color:     '#6B8F71',
    marginTop: 2,
  },
}
